import {Link, useParams, useLocation} from 'react-router-dom';
import {format} from 'date-fns';
import {useState, useEffect} from 'react';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  AlertCircle,
  Users,
  Edit,
  Check,
  X,
  FileText,
  ScrollText,
  FolderOpen,
  FileSignature,
  BarChart3,
} from 'lucide-react';
import {STALE_TIME_5_MIN} from '@common/constants/time';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {CODE_PERMISSIONS} from '@common/permissions';
import {resolveMenuItemEnabled} from '@common/menuFlags';
import {usePermissions} from '@common-web/hooks/use-permissions';
import {useClientFeaturesOrNull} from '@common-web/hooks/useClientFeatures';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {ProfileRow, ProfileSubheading} from '@common-web/components/profile-card';
import {
  CBS_ACTIVE_PILL_CLASS,
  CBS_INACTIVE_PILL_CLASS,
} from '@common-web/components/account/cbsAccountsPresentation';
import {AnalyzerGroupBadge} from '@common-web/components/account/AnalyzerGroupBadge';
import {trpc} from '@common-web/lib/trpc';
import {buildGroupMemberSearchPath} from '@common-web/hooks/useMemberSearchUrlState';
import {toast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {UpdateGroupModal} from './UpdateGroupModal';
import {RfcTab} from '@client/pages/account/tabs/RfcTab';
import {ProposalsTab} from '@client/pages/account/tabs/ProposalsTab';
import {DocumentsTab} from '@client/pages/account/tabs/DocumentsTab';
import {MsaTab} from '@client/pages/account/tabs/MsaTab';
import {UtilizationReportFrame} from '@client/pages/utilization/UtilizationReportFrame';

/**
 * Inline display + editor for the portal-managed open enrollment date. The date
 * is stored in Postgres `GroupMetadata` (not VBA); saving on a VBA-only group
 * adopts it into a local Group row server-side. Editable by internal and client
 * admins only.
 */
function OpenEnrollmentDateField({
  groupId,
  value,
  canEdit,
}: {
  groupId: string;
  value: string | null;
  canEdit: boolean;
}) {
  const utils = trpc.useUtils();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value ? value.slice(0, 10) : '');

  useEffect(() => {
    setDraft(value ? value.slice(0, 10) : '');
  }, [value]);

  const mutation = trpc.vba.groups.groupDetails.setOpenEnrollmentDate.useMutation({
    onSuccess: () => {
      toast({title: 'Success', description: 'Open enrollment date updated'});
      utils.vba.groups.groupDetails.getDetails.invalidate({groupId});
      setIsEditing(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update open enrollment date',
        variant: 'destructive',
      });
    },
  });

  return (
    <div>
      <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
        Open Enrollment Date
      </div>
      {isEditing ? (
        <div className='mt-1 flex items-center gap-2'>
          <Input
            type='date'
            aria-label='Open Enrollment Date'
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className='h-8 w-44'
            disabled={mutation.isPending}
          />
          <Button
            size='sm'
            variant='ghost'
            aria-label='Save open enrollment date'
            className='h-8 px-2'
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({groupId, openEnrollmentDate: draft || null})}
          >
            <Check className='h-4 w-4' />
          </Button>
          <Button
            size='sm'
            variant='ghost'
            aria-label='Cancel editing open enrollment date'
            className='h-8 px-2'
            disabled={mutation.isPending}
            onClick={() => {
              setDraft(value ? value.slice(0, 10) : '');
              setIsEditing(false);
            }}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ) : (
        <div className='flex items-center gap-2'>
          <span className='text-brand-navy font-medium'>
            {value ? format(new Date(value), 'MMM d, yyyy') : 'Not set'}
          </span>
          {canEdit && (
            <Button
              size='sm'
              variant='ghost'
              className='h-6 px-1 text-brand-gray hover:text-brand-navy'
              onClick={() => setIsEditing(true)}
            >
              <Edit className='h-3.5 w-3.5' />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export const GroupDetails = () => {
  const params = useParams<{groupId: string}>();
  const groupId = params.groupId;
  // When the user arrived from the Accounts list (a live VBA "Active" card),
  // the navigation carries a back target/label so the header link reads
  // "Back to Accounts" and returns there instead of the Groups list.
  const location = useLocation();
  const backNav = location.state as {backTo?: string; backLabel?: string} | null;
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const isGroupUser = useAuthStore((state) => state.isGroupUser());
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isInternal = useIsInternal();
  // Internal renders on the flat sand shell natively (no gradient), so it
  // doesn't depend on the index.css repaint override; client portals keep
  // the gradient wash.
  const shellClass = isInternal
    ? 'min-h-screen'
    : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5';
  const user = useAuthStore((state) => state.user);
  const permissions = usePermissions();
  // Mirrors the server-side `canEditGroupMetadata` gate: cross-tenant internal
  // users and tenant users holding `groups.admin_access`.
  const canEditOpenEnrollmentDate = isInternalUser
    ? permissions.has(CODE_PERMISSIONS.tenant.accessAll.key)
    : !!user?.clientProfile && permissions.has(CODE_PERMISSIONS.groups.adminAccess.key);
  const canEditGroupFields =
    !isGroupUser && (!isInternal || permissions.has(CODE_PERMISSIONS.groups.edit.key));
  const clientFeatures = useClientFeaturesOrNull();
  const canViewUtilization =
    !!user?.clientProfile &&
    resolveMenuItemEnabled('client.utilizationReport', clientFeatures) &&
    permissions.has(CODE_PERMISSIONS.pageAccess.viewUtilizationReport.key);
  const {data, isLoading, isFetching, error} = trpc.vba.groups.groupDetails.getDetails.useQuery(
    {groupId: groupId!},
    {
      enabled: !!groupId,
      staleTime: STALE_TIME_5_MIN, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      trpc: {context: {skipBatch: true}},
    },
  );

  // Live groups keep their pipeline paper trail: resolve the Accounts row this
  // group was built from so the profile can surface its RFC / Proposals /
  // Documents / MSA tabs after go-live. Internal-only, and absent for legacy
  // VBA groups that never went through the Accounts pipeline.
  const accountLookup = trpc.account.getByVbaGroupId.useQuery(
    {vbaGroupId: groupId!},
    {
      enabled: isInternal && !!groupId,
      staleTime: STALE_TIME_5_MIN,
      refetchOnWindowFocus: false,
    },
  );
  const accountsId = accountLookup.data?.accountsId ?? null;
  // Full account detail feeds the MSA tab (msa / msaEnvelopes / phase).
  const accountQuery = trpc.account.getById.useQuery(
    {accountsId: accountsId ?? ''},
    {enabled: !!accountsId},
  );
  const account = accountQuery.data;

  // Show error only if the main query fails completely
  if (error && !isLoading) {
    return (
      <div className={shellClass}>
        <div className='px-8 py-6 max-w-7xl mx-auto'>
          <Alert variant='destructive' className='border-red-200 bg-red-50'>
            <AlertCircle className='h-4 w-4' />
            <AlertDescription>{error?.message || 'Group not found'}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const getStatusBadge = (isActive: boolean) => {
    // Internal portal uses cbs-portal's soft status pills; client portals keep
    // the solid badges.
    if (isInternal) {
      return isActive ? (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${CBS_ACTIVE_PILL_CLASS}`}
        >
          Active
        </span>
      ) : (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${CBS_INACTIVE_PILL_CLASS}`}
        >
          Disenrolled
        </span>
      );
    }
    return isActive ? (
      <Badge className='bg-emerald text-white border-0 font-semibold px-3 py-1'>Active</Badge>
    ) : (
      <Badge className='bg-brand-gray text-white border-0 font-semibold px-3 py-1'>
        Disenrolled
      </Badge>
    );
  };

  // cbs-portal underline tab strip when internal (same pattern as the account
  // detail); client portals keep the boxed pill tabs.
  const tabsListClass = isInternal
    ? 'flex h-auto w-full justify-start gap-0 rounded-none border-b bg-transparent p-0'
    : 'bg-gray-100 p-1';
  const tabTriggerClass = isInternal
    ? '-mb-px rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-[13px] font-medium text-muted-foreground shadow-none transition-colors data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none'
    : 'data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm';

  return (
    <div className={shellClass}>
      <PageHeader
        title={data?.name || 'Group'}
        description={data ? `Group ID: ${data.groupId}` : undefined}
        icon={<Building2 className='h-6 w-6' />}
        isFetching={isFetching}
        isLoading={isLoading}
      >
        {data && canEditGroupFields && (
          <PageHeaderAction onClick={() => setIsUpdateModalOpen(true)}>
            <Edit className='h-4 w-4 mr-2' />
            Edit Group
          </PageHeaderAction>
        )}
        {/* Deep-link into Member Search scoped to this group (via the "Group name
            or ID" filter) so add-member and the full search toolset live in one
            place, instead of the siloed per-group members page. */}
        <PageHeaderAction tone='secondary' asChild>
          <Link to={buildGroupMemberSearchPath(groupId ?? '', data?.name)}>Members</Link>
        </PageHeaderAction>
        {backNav?.backTo ? (
          <PageHeaderAction tone='secondary' asChild>
            <Link to={backNav.backTo}>{backNav.backLabel ?? 'Back'}</Link>
          </PageHeaderAction>
        ) : (
          /* Only show Back to Groups if user has multiple groups */
          (!isGroupUser || (user?.groupProfiles && user.groupProfiles.length > 1)) && (
            <PageHeaderAction tone='secondary' asChild>
              <Link to='/groups'>Back to Groups</Link>
            </PageHeaderAction>
          )
        )}
      </PageHeader>

      {/* Main Content Area */}
      <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
        {data && (
          <div className='mb-6 flex flex-wrap items-center gap-2'>
            {getStatusBadge(data.isActive)}
            <AnalyzerGroupBadge groupProcessType={data.groupProcessType} />
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-6'>
          <TabsList className={tabsListClass}>
            <TabsTrigger value='details' className={tabTriggerClass}>
              <Building2 className='h-4 w-4 mr-2' />
              Group Details
            </TabsTrigger>
            {canViewUtilization && (
              <TabsTrigger value='utilization' className={tabTriggerClass}>
                <BarChart3 className='h-4 w-4 mr-2' />
                Utilization
              </TabsTrigger>
            )}
            {isInternal && accountsId && (
              <>
                <TabsTrigger value='rfc' className={tabTriggerClass}>
                  <FileText className='h-4 w-4 mr-2' />
                  RFC
                </TabsTrigger>
                <TabsTrigger value='proposals' className={tabTriggerClass}>
                  <ScrollText className='h-4 w-4 mr-2' />
                  Proposals
                </TabsTrigger>
                <TabsTrigger value='documents' className={tabTriggerClass}>
                  <FolderOpen className='h-4 w-4 mr-2' />
                  Documents
                </TabsTrigger>
                <TabsTrigger value='msa' className={tabTriggerClass}>
                  <FileSignature className='h-4 w-4 mr-2' />
                  MSA
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value='details'>
            {/* Internal: cbs-portal flat detail card; client portals keep the
                elevated card. */}
            <Card
              className={
                isInternal
                  ? 'rounded-xl border bg-card shadow-none overflow-hidden'
                  : 'border-0 shadow-lg bg-white overflow-hidden'
              }
            >
              <CardHeader
                className={
                  isInternal
                    ? 'border-b px-5 py-3'
                    : 'bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'
                }
              >
                <CardTitle
                  className={
                    isInternal
                      ? 'text-[12px] font-semibold uppercase tracking-wider text-muted-foreground'
                      : 'text-lg font-semibold text-brand-navy'
                  }
                >
                  Group Information
                </CardTitle>
              </CardHeader>
              <CardContent className={isInternal ? 'px-5 py-3 space-y-4' : 'p-6 space-y-6'}>
                {isLoading ? (
                  <div className='space-y-6'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {[...Array(6)].map((_, i) => (
                        <div key={i}>
                          <Skeleton className='h-4 w-24 mb-2' />
                          <Skeleton className='h-6 w-32' />
                        </div>
                      ))}
                    </div>
                    <div className='pt-6 border-t'>
                      <Skeleton className='h-6 w-32 mb-4' />
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        {[...Array(3)].map((_, i) => (
                          <div key={i}>
                            <Skeleton className='h-4 w-24 mb-2' />
                            <Skeleton className='h-6 w-40' />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className='pt-6 border-t'>
                      <Skeleton className='h-6 w-24 mb-4' />
                      <div className='space-y-2 ml-6'>
                        <Skeleton className='h-5 w-48' />
                        <Skeleton className='h-5 w-40' />
                      </div>
                    </div>
                  </div>
                ) : data ? (
                  isInternal ? (
                    /* cbs-portal ProfileRow convention for the internal
                       rendering; the client grid layout is below. */
                    <div>
                      <ProfileRow label='Group Name' value={data.name} />
                      <ProfileRow label='Group ID' value={data.groupId} mono />
                      <ProfileRow label='Agent ID' value={data.originalAgentId} mono />
                      <ProfileRow label='Federal Tax ID' value={data.federalId} mono />
                      <ProfileRow
                        label='Billing Cycle'
                        value={
                          data.billingCycle ? (
                            <span className='font-mono font-semibold tabular-nums'>
                              {data.billingCycle}
                            </span>
                          ) : null
                        }
                      />
                      <ProfileRow
                        label='Original Date'
                        value={
                          data.originalDate
                            ? format(new Date(data.originalDate), 'MMM d, yyyy')
                            : null
                        }
                      />
                      {data.disenrollDate ? (
                        <ProfileRow
                          label='Disenroll Date'
                          value={format(new Date(data.disenrollDate), 'MMM d, yyyy')}
                        />
                      ) : null}
                      <div className='py-2'>
                        <OpenEnrollmentDateField
                          groupId={data.groupId}
                          value={data.openEnrollmentDate}
                          canEdit={canEditOpenEnrollmentDate}
                        />
                      </div>
                      <ProfileSubheading>Primary Contact</ProfileSubheading>
                      <ProfileRow label='Name' value={data.primaryContact?.name} />
                      <ProfileRow label='Email' value={data.primaryContact?.email} />
                      <ProfileRow label='Phone' value={data.primaryContact?.phone} />
                      <ProfileSubheading>Address</ProfileSubheading>
                      <ProfileRow
                        label='Address'
                        value={
                          data.address?.line1 || data.address?.city ? (
                            <span>
                              {data.address?.line1 ? (
                                <span className='block'>{data.address.line1}</span>
                              ) : null}
                              {data.address?.line2 ? (
                                <span className='block'>{data.address.line2}</span>
                              ) : null}
                              {data.address?.city ||
                              data.address?.state ||
                              data.address?.zipCode ? (
                                <span className='block'>
                                  {data.address?.city ? `${data.address.city}, ` : ''}
                                  {data.address?.state} {data.address?.zipCode}
                                </span>
                              ) : null}
                            </span>
                          ) : null
                        }
                      />
                    </div>
                  ) : (
                    <>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Group Name
                          </div>
                          <div className='text-brand-navy font-medium'>{data.name}</div>
                        </div>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Group ID
                          </div>
                          <div className='text-brand-navy font-medium'>{data.groupId}</div>
                        </div>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Agent Id
                          </div>
                          <div className='text-brand-navy font-medium'>{data.originalAgentId}</div>
                        </div>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Federal Tax ID
                          </div>
                          <div className='text-brand-navy font-medium'>{data.federalId}</div>
                        </div>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Billing Cycle
                          </div>
                          <div className='font-mono font-bold text-brand-navy'>
                            {data.billingCycle || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Original Date
                          </div>
                          <div className='text-brand-navy font-medium'>
                            {data.originalDate
                              ? format(new Date(data.originalDate), 'MMM d, yyyy')
                              : 'N/A'}
                          </div>
                        </div>
                        <OpenEnrollmentDateField
                          groupId={data.groupId}
                          value={data.openEnrollmentDate}
                          canEdit={canEditOpenEnrollmentDate}
                        />
                        {data.disenrollDate && (
                          <div>
                            <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                              Disenroll Date
                            </div>
                            <div className='text-brand-navy font-medium'>
                              {format(new Date(data.disenrollDate), 'MMM d, yyyy')}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className='pt-6 border-t'>
                        <h4 className='font-semibold text-brand-navy mb-4'>Primary Contact</h4>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                          <div>
                            <div className='flex items-center gap-2 text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                              <Users className='h-3 w-3' />
                              Name
                            </div>
                            <div className='text-brand-navy font-medium'>
                              {data.primaryContact?.name || 'N/A'}
                            </div>
                          </div>
                          <div>
                            <div className='flex items-center gap-2 text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                              <Mail className='h-3 w-3' />
                              Email
                            </div>
                            <div className='text-brand-navy font-medium'>
                              {data.primaryContact?.email || 'N/A'}
                            </div>
                          </div>
                          <div>
                            <div className='flex items-center gap-2 text-xs font-semibold text-brand-gray uppercase tracking-wider mb-1'>
                              <Phone className='h-3 w-3' />
                              Phone
                            </div>
                            <div className='text-brand-navy font-medium'>
                              {data.primaryContact?.phone || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='pt-6 border-t'>
                        <h4 className='flex items-center gap-2 font-semibold text-brand-navy mb-4'>
                          <MapPin className='h-4 w-4 text-brand-blue' />
                          Address
                        </h4>
                        <div className='text-brand-navy font-medium ml-6'>
                          {data.address?.line1 && <div>{data.address.line1}</div>}
                          {data.address?.line2 && <div>{data.address.line2}</div>}
                          {(data.address?.city || data.address?.state || data.address?.zipCode) && (
                            <div>
                              {data.address?.city && `${data.address.city}, `}
                              {data.address?.state} {data.address?.zipCode}
                            </div>
                          )}
                          {!data.address?.line1 && !data.address?.city && (
                            <span className='text-brand-gray'>N/A</span>
                          )}
                        </div>
                      </div>
                    </>
                  )
                ) : (
                  <div className='text-center py-8 text-brand-gray'>No data available</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rendered for every group, including ones with no report. A group
              absent from the utilization spine (pending, never live, or
              managed outside CBS) answers NOT_FOUND and the report shows its
              own "No utilization data for this group yet", so there is nothing
              to gate on here. Radix unmounts inactive tab content, so the
              iframe only loads once the tab is opened. The pane scrolls with
              the page, so it takes a viewport-relative height rather than the
              report page's full-screen one. */}
          {canViewUtilization && data && (
            <TabsContent value='utilization'>
              <UtilizationReportFrame
                groupId={data.groupId}
                title={`${data.name} Utilization Report`}
                className='flex h-[calc(100vh-16rem)] min-h-[32rem] flex-col'
              />
            </TabsContent>
          )}

          {isInternal && accountsId && (
            <>
              <TabsContent value='rfc'>
                <RfcTab accountsId={accountsId} />
              </TabsContent>
              <TabsContent value='proposals'>
                <ProposalsTab accountsId={accountsId} />
              </TabsContent>
              <TabsContent value='documents'>
                <DocumentsTab accountsId={accountsId} onNavigateToRfc={() => setActiveTab('rfc')} />
              </TabsContent>
              <TabsContent value='msa'>
                {account ? (
                  <MsaTab
                    accountsId={accountsId}
                    companyName={account.companyName}
                    msa={account.msa}
                    msaEnvelopes={account.msaEnvelopes}
                    subStage={account.subStage}
                    lifecyclePhase={account.lifecyclePhase}
                  />
                ) : accountQuery.isError ? (
                  <Alert variant='destructive'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>
                      {accountQuery.error.message || 'Failed to load MSA details'}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Skeleton className='h-48 w-full' />
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Update Group Modal */}
      {data && (
        <UpdateGroupModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          groupData={{
            groupId: data.groupId,
            name: data.name,
            federalId: data.federalId,
            originalDate: data.originalDate,
            disenrollDate: data.disenrollDate,
            renewalDate: null, // Add if available in data
            billingCycle: data.billingCycle,
            address: data.address,
            primaryContact: data.primaryContact,
          }}
          onSuccess={() => {
            // Optionally add any additional actions on successful update
          }}
        />
      )}
    </div>
  );
};
